'use client'

import { PathParams, useFiltersStore } from '@/lib/store/filters-store'
import { useEffect, useMemo } from 'react'
import { useParams, useSearchParams } from 'next/navigation'
import { usePrevious } from 'ahooks'
import { getFiltersForContent } from '@/lib/filters/filters'
import { useRouter } from 'nextjs-toploader/app'
import { useUserContext } from '@/lib/contexts/UserContext'
import { useShallow } from 'zustand/react/shallow'
import { useSearchStore } from '@/lib/store/search-store'

/**
 * This client component is responsible for handling the router logic for the filter store.
 * It should be placed in the root layout.
 */
const FiltersStoreRouter = () => {
  const { mainCategory, category, subCategory, content } = useParams<PathParams>()
  const searchParams = useSearchParams()

  const filters = useFiltersStore(useShallow((state) => state.filters))
  const setFilters = useFiltersStore((state) => state.setFilters)
  const setParams = useFiltersStore((state) => state.setParams)
  const setSearchParams = useFiltersStore((state) => state.setSearchParams)

  const prevFilters = usePrevious(filters)

  const router = useRouter()

  // Sync router params with filters store
  useEffect(() => {
    console.debug('Router params changed', {
      mainCategory,
      category,
      subCategory,
      content,
    })
    setParams({
      mainCategory,
      category,
      subCategory,
      content,
    })
  }, [category, content, mainCategory, setParams, subCategory])

  // Sync search params with filters store
  useEffect(() => {
    console.debug('Search params changed', searchParams?.toString())
    setSearchParams(searchParams)
  }, [searchParams, setSearchParams])

  // Initialize filters store with URL params
  const { user, isAuthenticating } = useUserContext()

  const userIsPremium = useMemo(() => {
    if (!user) {
      return false
    }
    return (user?.team?.subscription?.plan?.price ?? 0) > 0
  }, [user])

  const searchQuery = useSearchStore((state) => state.search)
  const searchMainCategorySlug = useSearchStore((state) => state.searchMainCategorySlug)

  // The Main Category slug can also come from the search query
  const mainCategorySlugFromQuery = searchParams?.get('main_category')
  const resolvedMainCategorySlug = mainCategory ?? mainCategorySlugFromQuery ?? searchMainCategorySlug ?? undefined

  useEffect(() => {
    // Wait for user authentication, as some filters depend on it
    if (isAuthenticating) {
      console.debug('User is authenticating, skipping filter initialization')
      return
    }

    const availableFilters = getFiltersForContent({
      mainCategorySlug: resolvedMainCategorySlug,
      categorySlug: category,
      subCategorySlug: subCategory,
      contentSlug: content,
      searchQuery: searchParams?.toString(),
      userIsPremium,
      query: searchQuery,
    })

    setFilters(availableFilters)
  }, [
    category,
    content,
    isAuthenticating,
    searchQuery,
    resolvedMainCategorySlug,
    searchParams,
    setFilters,
    subCategory,
    userIsPremium,
  ])

  // When filters change, update the URL
  useEffect(() => {
    // Deep compare filters
    // const prevFiltersHash = hashFilters(prevFilters ?? [])
    // const filtersHash = hashFilters(filters)
    //
    // console.log({
    //   prevFilters,
    //   filters,
    // })
    //
    // // Skip routing if filters are the same. Also skip if filters are empty
    // if (!!prevFilters && prevFiltersHash !== filtersHash) {
    //   console.debug('Filters changed', prevFiltersHash, '>', filtersHash)
    //   const searchParams = toQuery(filters)
    //   const url = window.location.pathname + '?' + new URLSearchParams(searchParams).toString()
    //   console.debug('Navigating to', url)
    //   // router.push(url)
    // }
  }, [prevFilters, filters, router])

  useEffect(() => {
    console.debug('FiltersStoreRouter mounted')
  }, [])

  return null
}

FiltersStoreRouter.displayName = 'FiltersStoreRouter'

export default FiltersStoreRouter
